import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import watermarkImage from '../assets/coverzone-watermark.png';
import Header from '../Header';
import Sidebar from '../Sidebar';
import ClientInfoTab from './ClientInfoTab';
import AddressTab from './AddressTab';
import PersonOfInterestTab from './PersonOfInterestTab';
import { useClientData } from '../hooks/useClientData';
import { Client } from '../types';
import QuotesTab from './QuotesTab';
import PoliciesTab from './PoliciesTab';
import ClientPersonalDocuments from './ClientPersonalDocuments';
import ClientPersonalNotesTab from './ClientPersonalNotesTab';
import ClientPersonalInstructionTab from './ClientPersonalInstructionTab';
import axios from 'axios';
import ClientRenewalsTab from './ClientRenewalsTab';
import ClientReturnDebitsTab from './ClientReturnDebitsTab';
import ClientClaimsTab from './ClientClaimsTab';
import ClientPersonalEmailTab from '../components/ClientPersonalEmailTab';
import ClientPersonalWhatsAppTab from '../components/ClientPersonalWhatsAppTab';
import { usePermissions } from '../context/PermissionsContext';

interface TabDef {
  key: string;
  label: string;
  feature: string;
}

const TABS: TabDef[] = [
  { key: 'clientInfo',        label: 'Client Info',        feature: 'client_info' },
  { key: 'address',           label: 'Address',            feature: 'address' },
  { key: 'personOfInterest',  label: 'Person Of Interest', feature: 'person_of_interest' },
  { key: 'documents',         label: 'Documents',          feature: 'documents' },
  { key: 'email',             label: 'Email',              feature: 'email' },
  { key: 'whatsapp',          label: 'WhatsApp',           feature: 'whatsapp' },
  { key: 'notes',             label: 'Notes',              feature: 'notes' },
  { key: 'quotes',            label: 'Quotes',             feature: 'quotes' },
  { key: 'policies',          label: 'Policies',           feature: 'policies' },
  { key: 'instructions',      label: 'Instructions',       feature: 'instructions' },
  { key: 'claims',            label: 'Claims',             feature: 'claims' },
  { key: 'renewals',          label: 'Renewals',           feature: 'renewals' },
  { key: 'returnDebits',      label: 'Return Debits',      feature: 'return_debits' },
];

const Personal = () => {
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const { id: clientId } = useParams<{ id: string }>();
  const location = useLocation();
  const { user, client, isLoading, refreshClient } = useClientData(clientId);
  const [refreshKey, setRefreshKey] = useState(0);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const { can } = usePermissions();

  const navigate = useNavigate();
  const toggleSidebar = () => setIsSidebarVisible(!isSidebarVisible);

  const fetchAuditLogs = async (id: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return [];

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/clients/${id}/audit-logs`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      return [];
    }
  };

  const refreshAuditLogs = async () => {
    if (clientId) {
      const logs = await fetchAuditLogs(clientId);
      setAuditLogs(logs);
    }
  };

  // Handle client updates
  const handleClientUpdate = async (updatedClient: Client) => {
    console.log('👨‍💼 Parent: Client updated, forcing refresh...');

    if (refreshClient) {
      await refreshClient();
    }

    setRefreshKey(prev => prev + 1);

    await refreshAuditLogs();
    console.log('📝 Audit logs refreshed after client update');
  };

  // Initialize tab: use router state if present, fallback to clientInfo
  const instructionId = (location.state as any)?.instructionId;
  const [activeTab, setActiveTab] = useState(
    instructionId ? "instructions" : (location.state as any)?.activeTab || "clientInfo"
  );

  // ✅ ADD THIS: Listen for navigation changes (e.g. returning from AddPersonPage)
  useEffect(() => {
    const navState = location.state as any;
    if (navState?.activeTab && navState.activeTab !== activeTab) {
      setActiveTab(navState.activeTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const [selectedQuoteId, setSelectedQuoteId] = useState<number | null>(null);
  const [quoteVehicles, setQuoteVehicles] = useState<any[]>([]);
  const [quoteProductsLoading, setQuoteProductsLoading] = useState(false);

  // ---------------------------------------------------------------
  // Permission-aware tab list. If the currently active tab is not
  // permitted (e.g. landed here from a deep link), snap to the first
  // permitted tab.
  // ---------------------------------------------------------------
  const visibleTabs = TABS.filter(t => can(t.feature, 'read'));

  useEffect(() => {
    if (visibleTabs.length === 0) return;
    const isActiveVisible = visibleTabs.some(t => t.key === activeTab);
    if (!isActiveVisible) {
      console.log(
        `🔒 Active tab "${activeTab}" is not permitted for this role, switching to "${visibleTabs[0].key}"`
      );
      setActiveTab(visibleTabs[0].key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleTabs.map(t => t.key).join(','), activeTab]);

  useEffect(() => {
    const navState = location.state || {};
    const incomingQuoteId = navState.selectedQuoteId || navState.quoteId;

    if (incomingQuoteId && incomingQuoteId !== selectedQuoteId) {
      console.log("Personal.tsx - Selected quote changed via navigation:", incomingQuoteId);
      setSelectedQuoteId(incomingQuoteId);
      if (can('quotes', 'read')) {
        setActiveTab('quotes');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, selectedQuoteId]);

  useEffect(() => {
    const loadQuoteProducts = async () => {
      if (!selectedQuoteId) {
        setQuoteVehicles([]);
        return;
      }

      setQuoteProductsLoading(true);
      console.log("Personal.tsx - Loading products for quote:", selectedQuoteId);

      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error("No token");

        const vehRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/vehicles?quote_id=${selectedQuoteId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        console.log("Personal.tsx - Vehicles from API:", vehRes.data);
        setQuoteVehicles(vehRes.data.data || vehRes.data || []);
      } catch (err) {
        console.error("Personal.tsx - Error loading quote products:", err);
        setQuoteVehicles([]);
      } finally {
        setQuoteProductsLoading(false);
      }
    };

    loadQuoteProducts();
  }, [selectedQuoteId]);

  // Handle instructionId from location state (for navigation from Reports page)
  useEffect(() => {
    const navState = location.state || {};
    const incomingInstructionId = navState.instructionId;

    if (incomingInstructionId && can('instructions', 'read')) {
      console.log("Personal.tsx - Opening instruction from Reports:", incomingInstructionId);
      setActiveTab('instructions');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  useEffect(() => {
    if (clientId) {
      refreshAuditLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, location.key]);

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    console.error('Failed to load watermark image:', e);
  };

  const handleImageLoad = (type: string) => {
    console.log(`${type} image loaded successfully`);
  };

  const handleCreateQuoteNavigation = (currentClientId: string) => {
    console.log(`Navigating to create quote page for client: ${currentClientId}`);
    navigate(`/create-quote/${currentClientId}`);
  };

  if (isLoading || !user || !clientId) return <div>Loading...</div>;

  // If the user has no permitted tabs at all, we still render the shell so
  // they don't see a blank screen — the visibleTabs check below handles this.
  const noVisibleTabs = visibleTabs.length === 0;

  return (
    <div className="min-h-screen bg-gray-100 flex relative">
      <div className="fixed inset-0 flex items-center justify-center pointer-events-none z-0">
        <img
          src={watermarkImage}
          alt="Watermark"
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-100 h-100 opacity-5 z-0 select-none"
          onError={handleImageError}
          onLoad={() => handleImageLoad('Watermark')}
        />
      </div>
      <Sidebar isVisible={isSidebarVisible} toggleSidebar={toggleSidebar} />
      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarVisible ? 'ml-64' : 'ml-0'}`}>
        <Header
          title=""
          user={user}
          client={client}
          toggleSidebar={toggleSidebar}
          isSidebarVisible={isSidebarVisible}
          showClientNumber={true}
        />
        <div className="flex-1 p-6">
          <div className="bg-white rounded-lg shadow-md p-6 w-full max-w-[1600px] mx-auto">
            <h2 className="text-2xl font-bold mb-4">Personal Client Details</h2>

            {noVisibleTabs ? (
              <div className="text-center py-12 text-gray-500">
                You don't have access to any sections of this client.
              </div>
            ) : (
              <>
                <div className="flex space-x-2 mb-6 border-b border-gray-200 overflow-x-auto">
                  {visibleTabs.map((tab) => (
                    <button
                      key={tab.key}
                      className={`px-3 py-2 text-base font-medium transition-colors whitespace-nowrap ${
                        activeTab === tab.key
                          ? 'border-b-2 border-blue-500 text-blue-500'
                          : 'text-gray-500 hover:text-blue-500'
                      }`}
                      onClick={() => setActiveTab(tab.key)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                <div className="space-y-4">
                  {activeTab === 'clientInfo' && can('client_info', 'read') && (
                    <ClientInfoTab
                      key={`client-info-${refreshKey}`}
                      client={client}
                      clientId={clientId}
                      onClientUpdate={handleClientUpdate}
                    />
                  )}

                  {activeTab === 'address' && can('address', 'read') && (
                    <AddressTab
                      clientId={clientId}
                      onAddressChange={refreshAuditLogs}
                    />
                  )}

                  {activeTab === 'personOfInterest' && can('person_of_interest', 'read') && (
                    <PersonOfInterestTab clientId={clientId} />
                  )}

                  {activeTab === 'documents' && can('documents', 'read') && (
                    <ClientPersonalDocuments
                      clientId={clientId}
                      onDocumentChange={refreshAuditLogs}
                    />
                  )}

                  {activeTab === 'email' && can('email', 'read') && (
                    <ClientPersonalEmailTab
                      clientId={clientId}
                      user={user}
                      onEmailChange={refreshAuditLogs}
                    />
                  )}

                  {activeTab === 'whatsapp' && can('whatsapp', 'read') && (
                    <ClientPersonalWhatsAppTab
                      clientId={clientId}
                      user={user}
                      onWhatsAppChange={refreshAuditLogs}
                    />
                  )}

                  {activeTab === 'notes' && can('notes', 'read') && (
                    <ClientPersonalNotesTab
                      clientId={clientId}
                      auditLogs={auditLogs}
                      onRefreshAuditLogs={refreshAuditLogs}
                    />
                  )}

                  {activeTab === 'quotes' && can('quotes', 'read') && (
                    <QuotesTab
                      clientId={clientId}
                      onCreateQuote={handleCreateQuoteNavigation}
                    />
                  )}

                  {activeTab === 'policies' && can('policies', 'read') && (
                    <PoliciesTab clientId={clientId} />
                  )}

                  {activeTab === 'instructions' && can('instructions', 'read') && (
                    <ClientPersonalInstructionTab
                      clientId={clientId}
                      instructionId={instructionId}
                    />
                  )}

                  {activeTab === 'claims' && can('claims', 'read') && (
                    <ClientClaimsTab clientId={clientId} />
                  )}

                  {activeTab === 'renewals' && can('renewals', 'read') && (
                    <ClientRenewalsTab clientId={clientId} />
                  )}

                  {activeTab === 'returnDebits' && can('return_debits', 'read') && (
                    <ClientReturnDebitsTab clientId={clientId} />
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Personal;